/**
 * useBookings — load + CRUD for a single grid-date (with its night-tail).
 *
 *  - fetches BOTH `date` and `date+1` (because slots before 06:00 belong to
 *    the next calendar day but render on the current grid).
 *  - exposes save / cancel / move / archive — all delegate to v3 endpoints.
 *  - aborts in-flight requests when the date changes.
 *
 * Copied 1:1 from v3 hooks/useBookings.ts. The wire contract is identical
 * because apps/api is forked from v3.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../api/client";
import type { ApiList, ApiOk } from "../api/types";
import { fmtDateApi, addDays, isNightSlot } from "../utils/date";
import type { BookingsMap } from "./bookingTypes";
import { mapBooking, type RawBooking } from "./mapBooking";

interface BookingsResult {
  bookings: BookingsMap;
  loading: boolean;
  error: string | null;
  /** Ids of bookings that appeared since the last load (for entrance animation). */
  newIds: Set<number>;
  save: (payload: Record<string, unknown>, curDateStr: string) => Promise<void>;
  cancel: (id: number, reason: string) => Promise<void>;
  move: (id: number, questId: string, date: string, time: string, force?: boolean) => Promise<void>;
  archive: (id: number) => Promise<void>;
  reload: () => void;
  /** 03.09: день (YYYY-MM-DD), которому соответствует `bookings` — при смене curDate
   *  карта ещё старая, а loading уже/ещё false; потребителю нужно знать, чей это день. */
  loadedFor: string | null;
}

export function useBookings(curDate: Date): BookingsResult {
  const [bookings, setBookings] = useState<BookingsMap>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  /** Ids of bookings that just appeared (diff vs previous load) — for the
   *  grid's entrance animation when a booking shows up live. */
  const [newIds, setNewIds] = useState<Set<number>>(new Set());
  const [trigger, setTrigger] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const prevIdsRef = useRef<Set<number> | null>(null);
  const reload = useCallback(() => setTrigger((n) => n + 1), []);

  const load = useCallback((silent: boolean) => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const dateStr = fmtDateApi(curDate);
    const nextDateStr = fmtDateApi(addDays(curDate, 1));
    if (!silent) setLoading(true);
    setError(null);

    Promise.all([
      api<ApiList<RawBooking>>(`/api/bookings?date=${dateStr}`, { signal: ctrl.signal }),
      api<ApiList<RawBooking>>(`/api/bookings?date=${nextDateStr}`, { signal: ctrl.signal }),
    ])
      .then(([dayRes, nightRes]) => {
        if (ctrl.signal.aborted) return;
        const map: BookingsMap = {};
        for (const raw of dayRes.results) {
          if (!isNightSlot(raw.time)) {
            const b = mapBooking(raw, false);
            map[`${b.questId}_${b.time}`] = b;
          }
        }
        for (const raw of nightRes.results) {
          if (isNightSlot(raw.time)) {
            const b = mapBooking(raw, true);
            map[`${b.questId}_${b.time}`] = b;
          }
        }
        // Diff booking ids to flag just-appeared bookings (live entrance anim).
        const curIds = new Set<number>();
        for (const v of Object.values(map)) {
          const id = (v as { id?: number }).id;
          if (typeof id === "number") curIds.add(id);
        }
        if (prevIdsRef.current) {
          const nw = new Set<number>();
          for (const id of curIds) if (!prevIdsRef.current.has(id)) nw.add(id);
          setNewIds(nw);
        } else {
          setNewIds(new Set());
        }
        prevIdsRef.current = curIds;
        setBookings(map);
        setLoadedFor(dateStr);
        setLoading(false);
      })
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        setError(err instanceof Error ? err.message : "Ошибка загрузки");
        setLoading(false);
      });
  }, [curDate]);

  // Reset the "new" baseline on date change (don't animate the whole grid).
  useEffect(() => { prevIdsRef.current = null; }, [curDate]);

  useEffect(() => {
    load(false);
    return () => abortRef.current?.abort();
  }, [load, trigger]);

  // Live update — silent refetch every 15s while the grid is open & visible.
  useEffect(() => {
    const iv = window.setInterval(() => {
      if (typeof document === "undefined" || !document.hidden) load(true);
    }, 15000);
    return () => window.clearInterval(iv);
  }, [load]);

  const save = useCallback(
    async (payload: Record<string, unknown>, curDateStr: string) => {
      const id = payload["id"];
      const time = payload["time"] as string;
      const overrideDate = payload["overrideDate"] as string | null;

      // A48-канон: ночной 00:xx-слот бизнес-принадлежит СЛЕДУЮЩЕМУ дню — сдвиг +1
      // применяется к ЛЮБОЙ видимой дате, включая overrideDate из календаря шапки
      // (зеркало PUT /move; раньше create с overrideDate писал дату как есть и
      // расходился с move на сутки при одном и том же жесте).
      const base = overrideDate || curDateStr;
      const date = isNightSlot(time)
        ? fmtDateApi(addDays(new Date(`${base}T00:00:00Z`), 1))
        : base;

      const { questId: _qid, overrideDate: _od, ...rest } = payload;
      const body = { ...rest, date, quest_id: payload["questId"] };

      if (id) {
        await api<ApiOk>(`/api/bookings/${id}`, { method: "PUT", body });
      } else {
        await api<ApiOk>("/api/bookings", { method: "POST", body });
      }
      reload();
    },
    [reload],
  );

  const cancel = useCallback(
    async (id: number, reason: string) => {
      await api<ApiOk>(`/api/bookings/${id}`, {
        method: "DELETE",
        body: { cancel_reason: reason },
      });
      reload();
    },
    [reload],
  );

  const move = useCallback(
    async (id: number, questId: string, date: string, time: string, force?: boolean) => {
      await api<ApiOk>("/api/bookings/move", {
        method: "PUT",
        body: { id, quest_id: questId, date, time, force: force || undefined },   // 12.10: force — после ConflictOverride
      });
      reload();
    },
    [reload],
  );

  const archive = useCallback(
    async (id: number) => {
      await api<ApiOk>("/api/bookings/archive", { method: "PUT", body: { id } });
      reload();
    },
    [reload],
  );

  return { bookings, loading, error, newIds, save, cancel, move, archive, reload, loadedFor };
}
